import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useCart } from '../context/CartContext';
import { useMerchant } from '../context/MerchantContext';
import { getCartSuggestions, getProductTag } from '../services/SuggestionService';
import SuggestedProductCard from './SuggestedProductCard';

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 2000;
  
  @media (min-width: 768px) {
    align-items: center;
  }
`;

const ModalContainer = styled.div`
  background-color: white;
  width: 100%;
  max-width: 550px;
  border-radius: 24px 24px 0 0;
  padding: 1.5rem;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -10px 25px rgba(0,0,0,0.1);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }

  @media (min-width: 768px) {
    border-radius: 20px;
    margin: 1rem;
    animation: fadeIn 0.3s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
`;

const ScrollContainer = styled.div`
  overflow-y: auto;
  margin: 1rem -1.5rem;
  padding: 0 1.5rem;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #eee;
    border-radius: 10px;
  }
`;

const SuggestionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  padding: 0.5rem 0 1.5rem 0;

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 1rem;
  
  span {
    height: 4px;
    border-radius: 2px;
    flex: 1;
    background: #eee;
    
    &.active {
      background: ${props => props.color || '#e74c3c'};
    }
  }
`;

const PreCheckoutSuggestionsModal: React.FC = () => {
  const { state } = useCart();
  const { 
    restaurant, 
    merchantId, 
    menuCategories, 
    isPreCheckoutSuggestionsModalOpen, 
    setIsPreCheckoutSuggestionsModalOpen,
    setIsCheckoutModalOpen
  } = useMerchant();

  const suggestedProducts = useMemo(() => {
    const categories = menuCategories.length > 0 
      ? menuCategories 
      : (restaurant?.categories || (restaurant as any)?.restaurantCategories);
    
    if (!categories || !merchantId) return [];
    return getCartSuggestions(state.items, categories, merchantId, 8);
  }, [state.items, restaurant, merchantId, menuCategories]);

  if (!isPreCheckoutSuggestionsModalOpen) return null;

  const handleClose = () => {
    setIsPreCheckoutSuggestionsModalOpen(false);
  };

  const handleContinue = () => {
    setIsPreCheckoutSuggestionsModalOpen(false);
    setIsCheckoutModalOpen(true);
  };

  const getTag = (product: any) => {
    const category = (menuCategories.length > 0 ? menuCategories : restaurant?.categories)?.find(c => c.id === product.categoryId);
    return getProductTag(product, category?.name || '');
  };

  return (
    <ModalBackdrop onClick={handleClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <StepIndicator color={restaurant?.theme?.primary}>
          <span className="active"></span>
          <span></span>
        </StepIndicator>

        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h4 className="fw-bold m-0" style={{ fontSize: '1.25rem' }}>Deseja adicionar mais algo?</h4>
            <p className="text-muted small m-0 mt-1">
              Aproveite estas sugestões preparadas para você.
            </p>
          </div>
          <button 
            className="btn-close shadow-none" 
            onClick={handleClose}
            style={{ padding: '0.5rem' }}
          ></button>
        </div>
        
        <ScrollContainer>
          <SuggestionsGrid>
            {suggestedProducts.map((product) => (
              <SuggestedProductCard 
                key={product.id}
                item={product} 
                tag={getTag(product)}
              />
            ))}
          </SuggestionsGrid>
        </ScrollContainer>

        <div className="pt-3 bg-white border-top mt-auto">
          <button 
            className="btn w-100 py-3 fw-bold rounded-3 text-white shadow-sm transition-all"
            style={{ 
              backgroundColor: restaurant?.theme?.primary || '#e74c3c', 
              border: 'none',
              fontSize: '1rem'
            }}
            onClick={handleContinue}
          >
            Continuar para o checkout
          </button>
        </div>
      </ModalContainer>
    </ModalBackdrop>
  );
};

export default PreCheckoutSuggestionsModal;
